import type { HikingDifficulty } from "~/lib/api";
import { cn } from "~/lib/utils";
import { DifficultyIcon } from "~/components/ui/icons/difficulty-icons";
import { PillSelector } from "~/components/ui/pill-selector";

const DIFFICULTIES: { value: HikingDifficulty; label: string }[] = [
  { value: "easy", label: "Easy" },
  { value: "moderate", label: "Moderate" },
  { value: "hard", label: "Hard" },
  { value: "expert", label: "Expert" },
];

interface DifficultySelectorProps {
  value: HikingDifficulty | null;
  onChange: (value: HikingDifficulty | null) => void;
  disabled?: boolean;
}

export function DifficultySelector({ value, onChange, disabled }: DifficultySelectorProps) {
  return (
    <PillSelector.Root value={value} onChange={onChange} label="Trail difficulty" disabled={disabled}>
      {DIFFICULTIES.map(({ value: difficulty, label }) => (
        <PillSelector.Option key={difficulty} value={difficulty}>
          <DifficultyIcon difficulty={difficulty} className="size-3.5" />
          {label}
        </PillSelector.Option>
      ))}
    </PillSelector.Root>
  );
}

const BADGE_SIZES = {
  sm: { text: "text-xs", icon: "size-3.5", gap: "gap-0.5" },
  md: { text: "text-sm", icon: "size-4.5", gap: "gap-1.5" },
  lg: { text: "text-base", icon: "size-5.5", gap: "gap-2" },
} as const;

interface DifficultyBadgeProps {
  difficulty: HikingDifficulty;
  size?: keyof typeof BADGE_SIZES;
  /** Add white outline to icon for dark backgrounds */
  outline?: boolean;
  className?: string;
}

export function DifficultyBadge({ difficulty, size = "md", outline, className }: DifficultyBadgeProps) {
  const config = DIFFICULTIES.find((d) => d.value === difficulty);
  if (!config) return null;

  const { text, icon, gap } = BADGE_SIZES[size];

  return (
    <span className={cn("inline-flex items-center", gap, text, className)}>
      <DifficultyIcon difficulty={difficulty} outline={outline} className={icon} />
      {config.label}
    </span>
  );
}
